<template>
  <div class="c-theme-management w-full h-full">
    <CThemeManagementToolbar
      ref="toolbarWrapper"
      :custom-filter-components="dataFilterComponents"
      :hide-create="isImpact"
      :hide-view="!launchDarkly.allow_canvases_tables_columns_settings"
      :is-loading-items="isLoading"
      :metadata-filter-values="filters"
      :search="search"
      :tableColumnsSettings="tableColumnsSettings"
      @create="emit('create')"
      @move="onColumnsMove"
      @restoreColumns="restoreColumns"
      @search="onTableChange('searchChanged', $event)"
      @select="onColumnsSelect"
      @update:filter-values="onTableChange('filterValuesChanged', $event)"
    />
    <CTable
      ref="tableRef"
      class="mt-3"
      :columns="defaultColumns"
      :data="searchFilteredData"
      :initialSorting="initialSorting"
      :loading="isLoadingTable"
      :page-size="pageSize"
      :page-sizes="DEFAULT_PAGE_SIZE_OPTIONS"
      :scroller-style="{ height: `calc(100vh - 320px - ${themeManagementToolbarHeight || 0}px)` }"
      @page-changed="onTableChange('pageChanged', $event)"
      @page-size-changed="onTableChange('pageSizeChanged', $event)"
      @sort-changed="onTableChange('sortChanged', $event)"
    />
    <slot />
  </div>
</template>

<script setup lang="tsx">
import { h, provide, ref, onMounted, computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useElementSize } from '@vueuse/core'
import { type ColumnDef, createColumnHelper, type PaginationState, type SortingState } from '@tanstack/table-core'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CTable from '@lib/components/CTable/CTable.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CTooltipWrapper from '@lib/components/CTooltipWrapper/CTooltipWrapper.vue'
import CThemeManagementToolbar from './CThemeManagementToolbar.vue'
import CThemeManagementActionsCell from './CThemeManagementActionsCell.vue'
import CThemePreview from '@lib/components/CThemePreview/CThemePreview.vue'
import CCanvasDeleteDialogContent from '@lib/components/canvases/CCanvasDeleteDialogContent.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import useThemeVars from '@lib/composables/useThemeVars'
import { CLIENT_TYPE } from '@lib/constants/cdp.const'
import { formatDateTime, dayjs } from '@lib/util/date'
import { isPitcherWkWebView } from '@lib/util/userAgents'
import { isModifierClick } from '@lib/util/navigation.util'
import type { File, User } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { DEFAULT_PAGE_SIZE_OPTIONS } from '@lib/constants/tables.const'
import useToast from '@lib/composables/useToast'
import { useTableColumnsSettings } from '@lib/composables/tableColumnsSettings.use'
import usePagination from '@lib/composables/useCanvasTablesPagination'
import useCanvasTheme from '@lib/apps/canvas-builder/composables/useCanvasTheme'
import { isEqual, omit } from 'lodash-es'
import { CanvasThemeStatus, type CanvasThemeRetrieve } from '@lib/types/canvasTheme'
import CSwitch from '@lib/components/CSwitch/CSwitch.vue'
import { useCanvasStandardFilters } from '@lib/composables/useCanvasStandardFilters'

const props = defineProps<{
  storageKey?: string
  myUserId?: User['id']
  updater: (params: Partial<CanvasThemeRetrieve>) => Promise<CanvasThemeRetrieve>
  creator: (params: CanvasThemeRetrieve) => Promise<CanvasThemeRetrieve>
  fetcher: (params: Partial<CanvasThemeRetrieve>) => Promise<CanvasThemeRetrieve[]>
  listFiles: (params: any) => Promise<File[]>
  deleter: (id: CanvasThemeRetrieve['id']) => Promise<CanvasThemeRetrieve>
  byIdFetcher: (params: { id: CanvasThemeRetrieve['id']; fields?: string }) => Promise<CanvasThemeRetrieve>
  client: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
}>()

const emit = defineEmits<{
  open: [canvas: CanvasThemeRetrieve, newTab?: boolean]
  details: [canvas: CanvasThemeRetrieve]
  edit: [canvas: CanvasThemeRetrieve]
  create: []
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { t } = useI18n()

const { filterComponents: dataFilterComponents } = useCanvasStandardFilters({
  includeTagsFilter: false,
  includeTemplateFilter: false,
  includeOwnerFilter: true,
  ownerLabel: t('canvasUI.composables.useCanvasStandardFilters.createdBy'),
})

const themeVars = useThemeVars()
const { loadCustomFonts } = useCanvasTheme()
const confirmation = useConfirmation()
const { toast } = useToast()

const initialSorting = computed(() => {
  const orderingColumnId = DEFAULT_ORDERING.replace('-', '')
  const hasOrdering = tableColumnsSettings.value.some((item) => item.id === orderingColumnId && item.isSelected)

  if (!hasOrdering) {
    return undefined
  }

  const desc = DEFAULT_ORDERING.startsWith('-')

  return [{ id: orderingColumnId, desc }]
})

const tableRef = ref<InstanceType<typeof CTable> | null>(null)
const toolbarWrapper = ref<HTMLElement | null>(null)
const isCopyingId = ref<CanvasThemeRetrieve['id']>('')
const deletingInProgress = ref<Set<CanvasThemeRetrieve['id']>>(new Set())
const editingColumn = ref<{ id: CanvasThemeRetrieve['id']; column: string } | null>(null)
const fetchedData = ref<CanvasThemeRetrieve[]>([])
provide('availableThemes', fetchedData)
const isLoading = ref(false)
const isImpact = computed(() => props.client === CLIENT_TYPE.impact)
const DEFAULT_ORDERING = '-modified_at'

const { height: themeManagementToolbarHeight } = useElementSize(toolbarWrapper)

const initialFilters: Record<string, any> = {}

const {
  search,
  page,
  pageSize,
  selectedFilters: filters,
  ordering,
} = usePagination({
  storageKey: props.storageKey,
  initialFilters,
  initialOrdering: DEFAULT_ORDERING,
})

const fetchingParams = computed(() => ({
  page_size: pageSize.value,
  page: page.value,
  ordering: ordering.value,
  search: search.value,
}))

const searchFilteredData = computed(() => {
  let data = fetchedData.value

  // Apply search filter
  if (search.value) {
    const currSearch = (search.value ?? '').toLocaleLowerCase()
    data = data.filter((theme) => theme.name.toLocaleLowerCase().includes(currSearch) || false)
  }

  // Apply standard filters
  const hasActiveFilters = filters.value['is_draft'] !== undefined || filters.value['owned_by__name__icontains']

  if (hasActiveFilters) {
    data = data.filter((theme) => {
      // Draft filter
      const matchesDraft =
        filters.value['is_draft'] !== undefined && filters.value['is_draft'] !== null
          ? (theme.status === CanvasThemeStatus.DRAFT) === filters.value['is_draft']
          : true

      // Owner filter
      const matchesOwner = filters.value['owned_by__name__icontains']
        ? (theme.created_by?.name || theme.created_by?.email || '')
            .toLowerCase()
            .includes(filters.value['owned_by__name__icontains'].toLowerCase())
        : true

      return matchesDraft && matchesOwner
    })
  }

  return data
})

const isLoadingTable = computed(() => isLoading.value)

const columnHelper = createColumnHelper<CanvasThemeRetrieve>()

const defaultColumns = [
  columnHelper.accessor((row) => row.name, {
    id: 'name',
    cell: (cellCtx) =>
      h('div', { class: 'flex items-center gap-4' }, [
        h(CThemePreview, {
          class: 'rounded-1 pa-2',
          theme: cellCtx.row.original,
          width: 100,
          height: 75,
          placement: 'center-left',
        }),
        h(
          CTableInput,
          {
            modelValue: cellCtx.getValue(),
            state: isEditingCurrentColumn(cellCtx.row.original.id, cellCtx.column.id) ? 'edit' : 'view',
            readonly: isReadonly(cellCtx.row.original),
            class: 'cursor-pointer',
            valueClass: 'font-700 underline unerline-solid',
            'onUpdate:modelValue': (newValue) => {
              if (newValue !== cellCtx.row.original.name) {
                updateProperty(cellCtx.row.original, 'name', newValue)
              }
              clearEditingColumn()
            },
            onClick: (e: MouseEvent) => {
              emit('open', cellCtx.row.original, isModifierClick(e))
            },
            onEdit: () => {
              editingColumn.value = {
                id: cellCtx.row.original.id,
                column: cellCtx.column.id,
              }
            },
            onCancel: clearEditingColumn,
          },
          {
            suffix: () =>
              h(
                'div',
                {
                  class: 'flex items-center gap-2 ml-4',
                },
                [
                  props.client === 'admin' &&
                    cellCtx.row.original.is_default &&
                    h(
                      CTooltipWrapper,
                      {
                        tooltip: t('canvasUI.components.themes.CThemeManagement.actions.buttons.tooltips.defaultTheme'),
                        class: 'flex items-center align-center',
                      },
                      () => [
                        h(CIcon, {
                          icon: 'regular-palette-circle-check',
                          faType: 'fa-kit',
                          size: 'large',
                          color: themeVars.value.success,
                          class: 'cursor-default',
                          onClick: (e: MouseEvent) => e.stopPropagation(),
                        }),
                      ],
                    ),
                ],
              ),
          },
        ),
      ]),
    header: t('canvasUI.components.themes.CThemeManagement.nameTh'),
    sortingFn: (a, b) => a.original.name.toLocaleLowerCase().localeCompare(b.original.name.toLocaleLowerCase()),
  }),
  columnHelper.accessor((row) => row.status, {
    id: 'status',
    cell: (cellCtx) => (
      <div class="flex items-center">
        <CSwitch
          checked={cellCtx.getValue() === CanvasThemeStatus.READY}
          onChange={async (newValue: boolean) =>
            await onUpdateStatus(cellCtx.row.original, !newValue ? CanvasThemeStatus.DRAFT : CanvasThemeStatus.READY)
          }
          themeOverrides={{
            railColorActive: themeVars.value.success,
            railColor: themeVars.value.primary6,
          }}
        />
      </div>
    ),
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagementStatusCell.status.ready'),
    meta: {
      classes: 'w-[110px]',
    },
  }),
  columnHelper.accessor((row) => row.created_by, {
    id: 'owned_by__name',
    cell: (cellCtx) => {
      const createdBy = cellCtx.row.original.created_by?.name || cellCtx.row.original.created_by?.email || ''
      return h('div', { class: 'min-w-[150px]' }, createdBy)
    },
    header: t('canvasUI.components.themes.CThemeManagement.createdByTh'),
    sortingFn: (a, b) => {
      const createdByA = a.original.created_by.name || a.original.created_by.email || ''
      const createdByB = b.original.created_by.name || b.original.created_by.email || ''

      return createdByA.toLocaleLowerCase().localeCompare(createdByB.toLocaleLowerCase())
    },
  }),
  columnHelper.accessor((row) => row.modified_at, {
    id: 'modified_at',
    cell: (cellCtx) => {
      const modifiedBy = cellCtx.row.original.modified_by?.name || cellCtx.row.original.modified_by?.email
      const modifiedAt = formatDateTime(cellCtx.row.original.modified_at) as string
      const textColor = modifiedBy ? themeVars.value.text : themeVars.value.text3

      return h('div', { class: 'min-w-[250px]' }, [
        h('span', { style: { color: textColor } }, modifiedBy),
        ' ',
        h('span', {}, t('canvasUI.components.themes.CThemeManagement.atTh')),
        ' ',
        h('span', {}, modifiedAt),
      ])
    },
    header: t('canvasUI.components.themes.CThemeManagement.modifiedByTh'),
    sortingFn: (a, b) => {
      const dateComparison = dayjs(a.original.modified_at).diff(dayjs(b.original.modified_at))

      if (dateComparison !== 0) return dateComparison

      const modifiedByA = (a.original.modified_by?.name || a.original.modified_by?.email || '').toLocaleLowerCase()
      const modifiedByB = (b.original.modified_by?.name || b.original.modified_by?.email || '').toLocaleLowerCase()

      return modifiedByA.localeCompare(modifiedByB)
    },
  }),
  columnHelper.accessor((row) => row, {
    id: 'actions',
    cell: (cellCtx) =>
      h(CThemeManagementActionsCell, {
        hideCopy: isImpact.value,
        hideEdit: isReadonly(cellCtx.row.original) || isImpact.value,
        hideDelete: isReadonly(cellCtx.row.original) || isImpact.value,
        hideToggleDefault: cellCtx.row.original.status === CanvasThemeStatus.DRAFT || isImpact.value,
        hideViewNewTab: isPitcherWkWebView,
        isCopying: isCopyingId.value === cellCtx.row.original.id,
        isDeleting: deletingInProgress.value.has(cellCtx.cell.row.original.id),
        isDefault: cellCtx.row.original.is_default,
        onCopy: async () => {
          try {
            isCopyingId.value = cellCtx.row.original.id
            let copyIdx = 1
            let copyName = `${cellCtx.row.original.name} - ${t('canvasUI.components.themes.CThemeManagement.copy')}_${copyIdx}`
            while (fetchedData.value.some((theme) => theme.name === copyName)) {
              copyIdx++
              copyName = `${cellCtx.row.original.name} - ${t('canvasUI.components.themes.CThemeManagement.copy')}_${copyIdx}`
            }
            const toCopyFrom = await props.byIdFetcher({
              id: cellCtx.row.original.id,
            })

            const copy = await props.creator({ ...toCopyFrom, name: copyName, is_default: false })
            fetchedData.value = [copy, ...fetchedData.value] as any

            toast.success(
              t('canvasUI.components.themes.CThemeManagement.notifications.duplicate.success', {
                name: cellCtx.cell.row.original.name,
              }),
            )
          } catch (e) {
            console.error('theme copy failed:', e)

            toast.error(
              t('canvasUI.components.themes.CThemeManagement.notifications.duplicate.error', {
                name: cellCtx.cell.row.original.name,
              }),
            )
          } finally {
            isCopyingId.value = ''
          }
        },
        onEdit: () => emit('edit', cellCtx.row.original),
        onDelete: () => deleteTheme(cellCtx.row.original),
        onView: () => emit('open', cellCtx.row.original),
        onViewNewTab: () => emit('open', cellCtx.cell.row.original, true),
        onToggleDefault: async () => onToggleDefault(cellCtx.row.original),
      }),
    header: t('canvasUI.components.themes.CThemeManagement.actionsTh'),
    enableSorting: false,
  }),
] as ColumnDef<Record<string, any>, any>[]
const TABLE_COLUMNS_SETTINGS_KEY = 'ciq_themes_management_table_columns_settings'

const { tableColumnsSettings, onColumnsMove, onColumnsSelect, restoreColumns } = useTableColumnsSettings(
  TABLE_COLUMNS_SETTINGS_KEY,
  defaultColumns,
)

async function updateProperty<T extends keyof CanvasThemeRetrieve>(
  item: CanvasThemeRetrieve,
  property: T,
  newValue: CanvasThemeRetrieve[T],
) {
  try {
    const updatedItem = await props.updater({
      id: item.id,
      [property]: newValue,
    })
    const foundIndex = fetchedData.value.findIndex((theme) => theme.id === item.id)
    if (foundIndex !== -1) {
      fetchedData.value = [
        ...fetchedData.value.slice(0, foundIndex),
        updatedItem,
        ...fetchedData.value.slice(foundIndex + 1),
      ]
    }
  } catch (e) {
    console.error('theme update failed:', e)
  }
}

async function onUpdateStatus(item: CanvasThemeRetrieve, newValue: CanvasThemeStatus) {
  if (item.is_default && newValue === CanvasThemeStatus.DRAFT) {
    confirmation.warning({
      hasExit: false,
      hasCancel: false,
      title: t('canvasUI.components.themes.CThemeManagement.setDefaultTemplateToDraftDialog.title'),
      content:
        props.client === 'admin'
          ? t('canvasUI.components.themes.CThemeManagement.setDefaultTemplateToDraftDialog.adminContent', {
              name: item.name,
            })
          : t('canvasUI.components.themes.CThemeManagement.setDefaultTemplateToDraftDialog.impactContent', {
              name: item.name,
            }),
      confirmText: t('canvasUI.common.close'),
    })

    return
  }

  return await updateProperty(item, 'status', newValue)
}

async function deleteTheme(item: CanvasThemeRetrieve) {
  if (item.is_default) {
    confirmation.warning({
      hasExit: false,
      hasCancel: false,
      title: t('canvasUI.components.themes.CThemeManagement.deleteDefaultThemeDialog.title'),
      content: t('canvasUI.components.themes.CThemeManagement.deleteDefaultThemeDialog.content', {
        name: item.name,
      }),
      confirmText: t('canvasUI.common.close'),
    })
    return
  }

  confirmation.danger({
    hasExit: false,
    title: t('canvasUI.components.themes.CThemeManagement.deleteDialog.title'),
    content: () =>
      h(CCanvasDeleteDialogContent, {
        name: item?.name,
        texts: {
          name: t('canvasUI.components.themes.CThemeManagement.deleteDialog.content.name'),
          info:
            item.used_in_canvases_count && item.used_in_canvases_count > 0
              ? item.used_in_canvases_count > 1
                ? t('canvasUI.components.themes.CThemeManagement.deleteDialog.content.infoExtendedMany', {
                    count: item.used_in_canvases_count,
                  })
                : t('canvasUI.components.themes.CThemeManagement.deleteDialog.content.infoExtendedSingle')
              : t('canvasUI.components.themes.CThemeManagement.deleteDialog.content.info'),
        },
      }),
    confirmText: t('canvasUI.components.themes.CThemeManagement.deleteDialog.confirm'),
    cancelText: t('canvasUI.components.themes.CThemeManagement.deleteDialog.cancel'),
    onConfirm: async () => {
      try {
        deletingInProgress.value.add(item.id)
        await props.deleter(item.id)
        fetchedData.value = fetchedData.value.filter((canvas) => canvas.id !== item.id)

        toast.success(
          t('canvasUI.components.themes.CThemeManagement.notifications.delete.success', {
            name: item.name,
          }),
        )
      } catch (e) {
        console.error('theme deletion failed:', e)

        toast.error(
          t('canvasUI.components.themes.CThemeManagement.notifications.delete.error', {
            name: item.name,
          }),
        )
      } finally {
        deletingInProgress.value.delete(item.id)
      }
    },
  })
}

function isReadonly(item: CanvasThemeRetrieve) {
  return (isImpact.value && item.created_by.id !== props.myUserId) || !!editingColumn.value?.id
}

function isEditingCurrentColumn(id: CanvasThemeRetrieve['id'], column: string) {
  return editingColumn.value?.id === id && editingColumn.value?.column === column
}

function clearEditingColumn() {
  editingColumn.value = null
}

function onTableChange(
  event: 'pageChanged' | 'pageSizeChanged' | 'sortChanged' | 'searchChanged' | 'itemsChanged' | 'filterValuesChanged',
  state?: number | SortingState | PaginationState | string | Record<string, any>,
) {
  const initialParams = { ...fetchingParams.value }
  let change
  switch (event) {
    case 'pageChanged':
      change = state as PaginationState
      page.value = change.pageIndex + 1
      break
    case 'pageSizeChanged':
      change = state as number
      pageSize.value = change ?? fetchingParams.value.page_size
      page.value = 1
      break
    case 'sortChanged':
      ;[change] = state as SortingState
      ordering.value = change?.desc ? '-' + change.id : (change?.id ?? DEFAULT_ORDERING)
      page.value = 1
      break
    case 'searchChanged':
      change = state as string
      search.value = change
      page.value = 1
      break
    case 'filterValuesChanged':
      change = state as Record<string, any>
      filters.value = change
      page.value = 1
      break
    case 'itemsChanged':
      page.value = 1
      break
  }

  clearEditingColumn()

  if (event === 'itemsChanged' || !isEqual(initialParams, fetchingParams.value)) {
    refetch(fetchingParams.value)
  }
}

async function onToggleDefault(item: CanvasThemeRetrieve) {
  const setDefaultTemplate = async () => {
    try {
      const formerDefaultThemeIndex = fetchedData.value.findIndex((theme) => theme.is_default && theme.id !== item.id)
      let refetchThemes = false
      if (formerDefaultThemeIndex !== -1) {
        const formerDefaultTheme = fetchedData.value[formerDefaultThemeIndex]
        await updateProperty(formerDefaultTheme, 'is_default', false)
        refetchThemes = true
      }

      await updateProperty(item, 'is_default', true)

      if (refetchThemes) {
        // Refetch the themes to get the updated canvas counts
        await refetch(fetchingParams.value)
      }
      toast.success(
        t('canvasUI.components.themes.CThemeManagement.notifications.setDefaultTemplate.success', {
          name: item.name,
        }),
      )
    } catch (e) {
      console.error('set default template failed:', e)

      toast.error(
        t('canvasUI.components.themes.CThemeManagement.notifications.setDefaultTemplate.error', {
          name: item.name,
        }),
      )
    }
  }

  setDefaultTemplate()
}

async function refetch(params: { page_size: number; page: number; ordering: string; search: string }) {
  try {
    const data = await props.fetcher({
      ...(params?.search ? params : omit(params, 'search')),
    } as Partial<CanvasThemeRetrieve>)

    fetchedData.value = data
    // Use response.data or response.results depending on your API structure
    // fetchedData.value = isImpact.value
    //   ? data.filter((c) => !c.is_draft || (c.is_draft && c.created_by.id === props.myUserId))
    //   : data
  } catch (error) {
    console.error('Error fetching themes:', error)
    fetchedData.value = []
  }
}

onMounted(async () => {
  try {
    isLoading.value = true
    await Promise.allSettled([refetch(fetchingParams.value), loadCustomFonts(props.listFiles)])
    isLoading.value = false
  } catch (e) {
    console.error(e)
    isLoading.value = false
  }
})

defineExpose({
  refetch,
})
</script>
